import React, { useState, useEffect, useCallback, useRef } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import PageTitle from '../../components/PageTitle';

// Utility function to capitalize every word
const capitalizeWords = (str: string) => {
  return str.split(' ').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
};

interface Gallery {
  id: string;
  title: string;
  category: string;
  photos: Array<{
    url: string;
    title: string;
    fileName: string;
    description: string;
  }>;
  isPublished: boolean;
  status: string;
}

interface PhotoPopupProps {
  photos: Gallery['photos']; // All photos in the current gallery
  initialPhotoIndex: number; // Index of the photo to display initially
  galleryTitle: string;
  onClose: () => void;
}

const PhotoPopup: React.FC<PhotoPopupProps> = ({
  photos, initialPhotoIndex, galleryTitle, onClose
}) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(initialPhotoIndex);
  const imgRef = useRef<HTMLImageElement>(null);
  const startX = useRef(0);

  const currentPhoto = photos[currentPhotoIndex];

  const goToNextPhoto = useCallback(() => {
    setCurrentPhotoIndex((prevIndex) => (prevIndex + 1) % photos.length);
  }, [photos.length]);

  const goToPreviousPhoto = useCallback(() => {
    setCurrentPhotoIndex((prevIndex) => (prevIndex - 1 + photos.length) % photos.length);
  }, [photos.length]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') {
        goToNextPhoto();
      } else if (event.key === 'ArrowLeft') {
        goToPreviousPhoto();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [goToNextPhoto, goToPreviousPhoto]);

  const handleTouchStart = (e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    // Prevent scrolling while swiping on the image
    e.preventDefault();
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const endX = e.changedTouches[0].clientX;
    const diffX = startX.current - endX;

    if (diffX > 50) { // Swiped left
      goToNextPhoto();
    } else if (diffX < -50) { // Swiped right
      goToPreviousPhoto();
    }
  };

  if (!currentPhoto) return null; // Should not happen if photos array is valid

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="max-w-4xl w-full bg-gray-800 rounded-lg overflow-hidden relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white hover:text-gray-300 z-10 p-2 rounded-full bg-black bg-opacity-50 hover:bg-opacity-75 transition-opacity"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Navigation Buttons */}
        {photos.length > 1 && (
          <>
            <button
              onClick={goToPreviousPhoto}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-black text-7xl z-10 drop-shadow-lg focus:outline-none"
              aria-label="Previous photo"
            >
              &#10094;
            </button>
            <button
              onClick={goToNextPhoto}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-black text-7xl z-10 drop-shadow-lg focus:outline-none"
              aria-label="Next photo"
            >
              &#10095;
            </button>
          </>
        )}

        <div
          className="relative aspect-w-16 aspect-h-9"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <img
            ref={imgRef}
            src={currentPhoto.url}
            alt={currentPhoto.title || currentPhoto.fileName}
            className="w-full h-full object-contain"
          />
        </div>
        <div className="p-4 bg-gray-800">
          <h3 className="text-xl font-semibold text-white mb-2">{galleryTitle}</h3>
          {currentPhoto.description && (
            <p className="text-gray-300">{currentPhoto.description}</p>
          )}
        </div>
      </div>
    </div>
  );
};

const PhotoGallery = () => {
  const [galleries, setGalleries] = useState<Gallery[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedPhotoInfo, setSelectedPhotoInfo] = useState<{ photo: Gallery['photos'][0], galleryTitle: string, allPhotos: Gallery['photos'], initialPhotoIndex: number } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(
      collection(db, 'photo-gallery'),
      where('isPublished', '==', true)
      // Removed the status filter to see if it's affecting our results
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const galleriesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Gallery));

        console.log('Gallery Data:', galleriesData);

        // Extract all categories and ensure they're not empty
        const allCategories = galleriesData.map(gallery => gallery.category);
        console.log('All Categories:', allCategories);

        const uniqueCategories = [...Array.from(
          new Set(allCategories)
        )
        .filter(category => category && category.trim() !== '')
        .sort()];

        console.log('Unique Categories:', uniqueCategories);

        setGalleries(galleriesData);
        setCategories(uniqueCategories);
        
        // Set "All" as default category
        if (!selectedCategory) {
          setSelectedCategory('All');
        }
        
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching galleries:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [selectedCategory]);

  const filteredGalleries = selectedCategory === 'All' 
    ? galleries 
    : galleries.filter(gallery => gallery.category === selectedCategory);

  return (
    <div className="bg-gray-900 min-h-screen">
      <PageTitle
        title="Photo Gallery"
        subtitle="Explore Our Collection Of Memorable Moments And Impactful Events"
      />

      {/* Category Navigation */}
      <div className="bg-gray-800 sticky top-0 z-10 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-1 overflow-x-auto py-3 scrollbar-hide">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 whitespace-nowrap rounded-full transition-colors ${
                  selectedCategory === category
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-300 hover:text-white hover:bg-gray-700'
                }`}
              >
                {capitalizeWords(category)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Gallery Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center text-white">Loading Galleries...</div>
        ) : filteredGalleries.length === 0 ? (
          <div className="text-center text-white">No Galleries Found In This Category</div>
        ) : (
          <div className="space-y-12">
            {filteredGalleries.map(gallery => (
              <div key={gallery.id} className="space-y-4">
                <h2 className="text-2xl font-bold text-white">{capitalizeWords(gallery.title)}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {gallery.photos && gallery.photos.map((photo, index) => (
                    <div
                      key={`${gallery.id}-${index}`}
                      className="relative group overflow-hidden rounded-lg shadow-lg aspect-w-16 aspect-h-9 cursor-pointer"
                      onClick={() => setSelectedPhotoInfo({
                        photo: photo,
                        galleryTitle: gallery.title,
                        allPhotos: gallery.photos,
                        initialPhotoIndex: index
                      })}
                    >
                      <img
                        src={photo.url}
                        alt={photo.title || photo.fileName}
                        className="w-full h-64 object-cover transform transition-transform duration-300 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-opacity duration-300">
                        <div className="absolute bottom-0 left-0 right-0 p-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          {photo.description && (
                            <p className="text-sm">{photo.description}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Photo Popup */}
      {selectedPhotoInfo && (
        <PhotoPopup
          photos={selectedPhotoInfo.allPhotos}
          initialPhotoIndex={selectedPhotoInfo.initialPhotoIndex}
          galleryTitle={selectedPhotoInfo.galleryTitle}
          onClose={() => setSelectedPhotoInfo(null)}
        />
      )}
    </div>
  );
};

export default PhotoGallery;
